import { useState } from 'react';
import { Button, Card, Modal } from '@playerbase/ui';
import type { PlaytestApiKey } from '@playerbase/types';

interface ApiKeyCardProps {
  meta: PlaytestApiKey | null;
  // The full plaintext key, set transiently after a rotate. While this is
  // non-null, the reveal banner is visible. Dismiss to drop back to the
  // prefix-only view; the plaintext is then lost until the next rotate.
  plaintext: string | null;
  loading: boolean;
  rotating: boolean;
  error: string | null;
  onRotate: () => void | Promise<void>;
  onDismissReveal: () => void;
}

export function ApiKeyCard({
  meta,
  plaintext,
  loading,
  rotating,
  error,
  onRotate,
  onDismissReveal,
}: ApiKeyCardProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleCopyKey(): Promise<void> {
    if (plaintext === null) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  function handleRotateClick(): void {
    if (meta !== null) {
      setConfirmOpen(true);
      return;
    }
    void onRotate();
  }

  async function handleConfirmRotate(): Promise<void> {
    setConfirmOpen(false);
    await onRotate();
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>API key</Card.Title>
        <Card.Description>
          One key per game, signed into every request from your build. The
          pairing handshake and event-firing snippets below both reuse it.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        {plaintext !== null ? (
          <div className="flex flex-col gap-2 rounded-md border border-accent/60 bg-accent/5 p-3 shadow-[0_0_10px_rgba(168,224,99,0.3)]">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-accent">
                New API key
              </span>
              <Button variant="ghost" onClick={onDismissReveal}>
                Done, hide it
              </Button>
            </div>
            <code className="overflow-x-auto rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-sm">
              {plaintext}
            </code>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-text-muted">
                Copy this now - it won&rsquo;t be shown again. After dismiss
                you&rsquo;ll only see the prefix; rotate again to issue a
                fresh one.
              </p>
              <Button variant="ghost" onClick={() => void handleCopyKey()}>
                {copied ? 'Copied!' : 'Copy key'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              API key
            </span>
            {loading ? (
              <span className="text-sm text-text-muted">Loading…</span>
            ) : meta === null ? (
              <span className="text-sm text-text-muted">
                No key yet. Generate one to start signing requests.
              </span>
            ) : (
              <code className="rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-sm">
                {meta.keyPrefix}
                <span className="text-text-muted">…</span>
              </code>
            )}
            <div className="ml-auto">
              <Button
                variant={meta === null ? 'primary' : 'ghost'}
                onClick={handleRotateClick}
                disabled={rotating || loading}
              >
                {rotating
                  ? 'Working…'
                  : meta === null
                    ? 'Generate'
                    : 'Regenerate'}
              </Button>
            </div>
          </div>
        )}

        {error !== null ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : null}
      </Card.Content>

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!rotating) setConfirmOpen(false);
        }}
        labelledById="regenerate-api-key"
        busy={rotating}
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id="regenerate-api-key">Regenerate API key</Card.Title>
            <Card.Description>
              The current key stops working immediately. Any game build still
              shipping it will stop sending events until a new build with the
              new key ships. Paired testers themselves are unaffected.
            </Card.Description>
          </Card.Header>
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={rotating}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleConfirmRotate()}
              disabled={rotating}
            >
              {rotating ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </Card>
  );
}
